/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Tooltip } from "@material-ui/core";
import classnames from "classnames";

import { Button, Confirm, useNotify, useRedirect } from "react-admin";

import { ResourceType, ServiceType } from "../../../utils/constants";
import { DeleteIcon } from "../../../components/common/iconography";
import { logError, logHistory } from "../../../utils/appUtils";
import { deepRedirect } from "../../../utils/utils";
import { useDeleteButtonStyles } from "../../../components/common/useDeleteButtonStyles";
import {
  ServiceDeleteAction,
  deleteOneService,
  fetchChargeRuleUsage,
} from "./ServiceUtils";

export const DeepDeleteButton = ({
  record,
  backPath,
  className,
  isDeleting,
  setIsDeleting,
  isSaving,
  isYardTemplate,
  ...props
}) => {
  const classes = useDeleteButtonStyles();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [chargeRuleUsage, setChargeRuleUsage] = useState({
    hasActive: false,
    hasAny: false,
  });

  // Check for active charge rules on component mount. This only drives the
  // tooltip/disabled state - the delete itself classifies the service again
  // and refuses on its own.
  useEffect(() => {
    if (isYardTemplate || !record?.id) return;
    fetchChargeRuleUsage(record.id)
      .then(setChargeRuleUsage)
      .catch((error) =>
        logError("DeepDeleteButton:checkChargeRules", error, notify),
      );
  }, [record]);

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleDelete = async (event) => {
    setIsDeleting(true);

    try {
      // Which removal a service may take - and whether it may take one at all -
      // is decided in ServiceUtils, the same call the tab's bulk delete makes.
      // The classification is read fresh here rather than from the mount check
      // above, which may still be in flight, may have failed into its
      // permissive default, or may be stale in this handler's closure.
      const { action, reason } = await deleteOneService(record);

      if (action === ServiceDeleteAction.BLOCKED) {
        notify(`Service not deleted: ${reason}.`, "warning");
        // The refusal is news about the record, so the button catches up with
        // it: an active billing rule added since mount turns Delete into the
        // disabled "Used in billing rules" state instead of leaving a button
        // that only refuses on the next click.
        fetchChargeRuleUsage(record.id)
          .then(setChargeRuleUsage)
          .catch((error) =>
            logError("DeepDeleteButton:refreshChargeRules", error, false),
          );
        return;
      }

      if (action === ServiceDeleteAction.HARD) {
        // Hard deletes are logged on the account, since the service row itself
        // is gone. A yard template service has no account to log against.
        if (!isYardTemplate)
          logHistory({
            parentId: record.accountId,
            parentType: ResourceType.ACCOUNT,
            text: `Service ${record.name} deleted.`,
          });
      } else {
        logHistory({
          parentId: record.id,
          parentType: ResourceType.SERVICE,
          text: "Service deleted.",
        });
      }

      notify("Service deleted successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError(
        "accounts:AccountEdit:EditRemoveButton:handleRemove",
        error,
        notify,
        redirect,
      );
    } finally {
      setIsDeleting(false);
      setOpen(false);
    }
  };

  // Determine if delete button should be disabled
  const isDeleteDisabled =
    isDeleting || isSaving || record?.name === ServiceType.ACCESSORIAL_CHARGES;

  if (chargeRuleUsage.hasActive) {
    return (
      <Tooltip title="Used in billing rules" arrow placement="left">
        <span>
          <Button
            {...props}
            label="Delete"
            disabled
            className={classnames(
              "ra-delete-button",
              classes.deleteButton,
              className,
            )}
          >
            <DeleteIcon />
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <>
      <Button
        {...props}
        label="Delete"
        disabled={isDeleteDisabled}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title={"Delete Confirmation"}
        content={
          "You will not lose any data, but removing an active Service cannot be undone. Continue?"
        }
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
